import React, { Suspense, lazy } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LogOut, Loader2 } from "lucide-react";
import { CountryScopeProvider, useCountryScope } from "@/context/CountryContext";
import AdminLogo, { useBrandSettings } from "@/components/AdminLogo";

// ChangePasswordButton stays eager — it's rendered in the sticky header (not tab content)
import ChangePasswordButton from "./tabs/ChangePasswordButton";
import NotificationBell from "@/components/NotificationBell";

// Tab modules — lazy-loaded so each 19 tab chunk downloads only when opened
const CandidatesTab = lazy(() => import("./tabs/CandidatesTab"));
const AIBiodataTab = lazy(() => import("./tabs/AIBiodataTab"));
const BookingsDashboardTab = lazy(() => import("./tabs/BookingsDashboardTab"));
const BookingsTab = lazy(() => import("./tabs/BookingsTab"));
const InProcessTab = lazy(() => import("./tabs/InProcessTab"));
const CountriesTab = lazy(() => import("./tabs/CountriesTab"));
const SkillsTab = lazy(() => import("./tabs/SkillsTab"));
const BiodataSchemaTab = lazy(() => import("./tabs/BiodataSchemaTab"));
const WebsiteBuilderTab = lazy(() => import("./tabs/WebsiteBuilderTab"));
const HeroSliderTab = lazy(() => import("./tabs/HeroSliderTab"));
const PDFTemplateTab = lazy(() => import("./tabs/PDFTemplateTab"));
const TemplatesTab = lazy(() => import("./tabs/TemplatesTab"));
const SeoTab = lazy(() => import("./tabs/SeoTab"));
const TextEditorTab = lazy(() => import("./tabs/TextEditorTab"));
const EmailManagementTab = lazy(() => import("./tabs/EmailManagementTab"));
const HealthTab = lazy(() => import("./tabs/HealthTab"));
const NotificationsTab = lazy(() => import("./tabs/NotificationsTab"));
const SettingsTab = lazy(() => import("./tabs/SettingsTab"));
const UserManagementTab = lazy(() => import("./tabs/UserManagementTab"));
const EmployerPreviewTab = lazy(() => import("./tabs/EmployerPreviewTab"));
const AuditLogTab = lazy(() => import("./tabs/AuditLogTab"));
const EmployersTab = lazy(() => import("./tabs/EmployersTab"));
const PageBuilderTab = lazy(() => import("./tabs/PageBuilderTab"));
const BroadcastWATab = lazy(() => import("./tabs/BroadcastWATab"));

// Master list of tab keys ↔ labels shown in the AdminDashboard TabsList.
// Also used by UserManagementTab's Permissions dialog so the Super Admin
// can pick exactly which tabs an individual account may open.
export const ALL_ADMIN_TABS = [
  { key: "candidates",    label: "Candidates" },
  { key: "in-process",    label: "In Process" },
  { key: "ai-biodata",    label: "AI Biodata" },
  { key: "bookings-dashboard", label: "Bookings Dashboard" },
  { key: "bookings",      label: "Bookings" },
  { key: "employers",     label: "Employers" },
  { key: "countries",     label: "Countries" },
  { key: "skills",        label: "Skills" },
  { key: "schema",        label: "Biodata Schema" },
  { key: "builder",       label: "Website Builder" },
  { key: "pages",         label: "Page Builder" },
  { key: "hero",          label: "Hero Slider" },
  { key: "pdf-template",  label: "PDF Template" },
  { key: "templates",     label: "Templates" },
  { key: "broadcast-wa",  label: "WA Broadcast" },
  { key: "seo",           label: "SEO" },
  { key: "text",          label: "Text Editor" },
  { key: "email",         label: "Email" },
  { key: "health",        label: "Health" },
  { key: "notifications", label: "Notifications" },
  { key: "settings",      label: "Settings" },
  { key: "users",         label: "Users" },
  { key: "audit",         label: "Activity Log" },
  { key: "preview",       label: "Employer Preview" },
];

function TabFallback() {
  return (
    <div className="flex items-center justify-center py-24 text-zinc-500" data-testid="tab-loading">
      <Loader2 className="animate-spin mr-2" size={18} />
      <span className="text-sm">Loading module…</span>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <CountryScopeProvider>
      <AdminDashboardInner />
    </CountryScopeProvider>
  );
}

function AdminDashboardInner() {
  const { user, logout } = useAuth();
  const { activeSlug, activeCountry, countries, setSlug } = useCountryScope();
  const { data: brand } = useBrandSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const companyName = brand?.company_name || "Adelina";
  const logoUrl = brand?.logo_url
    ? (brand.logo_url.startsWith("http") ? brand.logo_url : `${process.env.REACT_APP_BACKEND_URL || ""}${brand.logo_url}`)
    : null;
  return (
    <div className="adelina-admin-mobile min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-50 relative">
      {/* Ambient Adelina logo watermark — extremely subtle, fixed behind content */}
      {logoUrl && (
        <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0" aria-hidden="true">
          <img
            src={logoUrl}
            alt=""
            className="w-full max-w-[640px] max-h-[640px] object-contain opacity-[0.045]"
            style={{ filter: "blur(2px)" }}
            data-testid="admin-bg-watermark"
          />
        </div>
      )}
      <div className="relative z-10">
      <header className="bg-white/85 backdrop-blur-xl border-b border-zinc-200/70 sticky top-0 z-30 shadow-[0_1px_0_rgba(0,0,0,0.02)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3 group min-w-0" data-testid="admin-brand-link">
            <AdminLogo size={40} variant="light" className="ring-1 ring-zinc-200 group-hover:ring-[color:var(--brand-primary,#b8860b)] transition-all" />
            <div className="leading-tight">
              <div className="font-serif text-base sm:text-lg tracking-tight text-zinc-950">{companyName} <span className="text-[color:var(--brand-primary,#b8860b)] font-normal">CMS</span></div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 font-medium mt-0.5">
                {activeCountry ? `${activeCountry.name} Dashboard` : "Global Admin Panel"}
              </div>
            </div>
          </Link>
          <div className="w-full sm:w-auto flex flex-wrap items-center justify-between sm:justify-end gap-2 text-sm">
            <div className="min-w-[120px] flex-1 sm:flex-none">
              <Select value={activeSlug || "__all__"} onValueChange={(v) => setSlug(v === "__all__" ? null : v)}>
                <SelectTrigger className="h-9 w-full min-w-[120px] sm:min-w-[200px] text-xs bg-white border-zinc-200 hover:border-zinc-300 transition-colors" data-testid="country-scope-select">
                  <SelectValue placeholder="All Countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">🌐 All Countries</SelectItem>
                  {countries.map((c) => (
                    <SelectItem key={c.slug} value={c.slug} data-testid={`country-scope-${c.slug}`}>
                      {c.code ? `${c.code} · ` : ""}{c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-zinc-500 hidden lg:inline text-[12.5px]">{user?.email}</span>
            <NotificationBell variant="light" />
            <ChangePasswordButton />
            <button onClick={logout} className="inline-flex items-center gap-1.5 px-2.5 sm:px-3.5 py-2 sm:py-1.5 rounded-full border border-zinc-300 text-[12.5px] font-medium hover:bg-zinc-950 hover:text-white hover:border-zinc-950 transition-all" data-testid="admin-logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        {activeCountry && (
          <div className="max-w-7xl mx-auto px-6 pb-3 flex items-center gap-2 text-xs" data-testid="country-scope-banner">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-[0.16em] bg-[#0a0a0a] text-[#d4a017] font-semibold">Scoped</span>
            <span className="text-zinc-600">Showing only <span className="font-semibold text-zinc-950">{activeCountry.name}</span> candidates &amp; bookings.</span>
            <button onClick={() => setSlug(null)} className="text-[color:var(--brand-primary,#b8860b)] underline hover:no-underline">Clear scope</button>
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-10">
        {(() => {
          // Filter visible tabs based on the current user's allowed_tabs list.
          // Super admin always sees everything; a null/undefined list also
          // falls back to "all tabs" so we do not accidentally lock users out.
          const isSuper = user?.role === "super_admin";
          const list = user?.allowed_tabs;
          const visible = (isSuper || !Array.isArray(list))
            ? ALL_ADMIN_TABS
            : ALL_ADMIN_TABS.filter((t) => list.includes(t.key));
          const defaultTab = visible.find((t) => t.key === "candidates")?.key || visible[0]?.key || "candidates";
          // `?tab=...` deep-linking so TemplatesTab's DeepLink buttons (and any
          // future shareable admin URL) can open a specific tab directly.
          const requestedTab = searchParams.get("tab");
          const initialTab = requestedTab && visible.some((t) => t.key === requestedTab)
            ? requestedTab
            : defaultTab;
          const componentMap = {
            "candidates":    CandidatesTab,
            "in-process":    InProcessTab,
            "ai-biodata":    AIBiodataTab,
            "bookings-dashboard": BookingsDashboardTab,
            "bookings":      BookingsTab,
            "employers":     EmployersTab,
            "countries":     CountriesTab,
            "skills":        SkillsTab,
            "schema":        BiodataSchemaTab,
            "builder":       WebsiteBuilderTab,
            "pages":         PageBuilderTab,
            "hero":          HeroSliderTab,
            "pdf-template":  PDFTemplateTab,
            "templates":     TemplatesTab,
            "broadcast-wa":  BroadcastWATab,
            "seo":           SeoTab,
            "text":          TextEditorTab,
            "email":         EmailManagementTab,
            "health":        HealthTab,
            "notifications": NotificationsTab,
            "settings":      SettingsTab,
            "users":         UserManagementTab,
            "audit":         AuditLogTab,
            "preview":       EmployerPreviewTab,
          };
          const navigateToTab = (v) => {
            const next = new URLSearchParams(searchParams);
            next.set("tab", v);
            setSearchParams(next, { replace: true });
          };
          return (
            <Tabs defaultValue={initialTab} value={initialTab} onValueChange={navigateToTab}>
              <div className="md:hidden mb-5">
                <label htmlFor="mobile-admin-tabs" className="block mb-1.5 text-xs font-semibold text-zinc-700">Admin section</label>
                <select id="mobile-admin-tabs" data-testid="mobile-admin-section-picker" value={initialTab} onChange={(e) => navigateToTab(e.target.value)} className="block w-full min-h-11 rounded-xl border border-zinc-300 bg-white px-3 py-2 text-base text-zinc-950 shadow-sm">
                  {visible.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <TabsList className="hidden md:flex mb-8 bg-white/80 backdrop-blur border border-zinc-200 p-1.5 rounded-2xl shadow-sm text-[12.5px] font-medium tracking-tight flex-wrap justify-start gap-1 h-auto w-full">
                {visible.map((t) => (
                  <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`} className="rounded-full px-3.5">{t.label}</TabsTrigger>
                ))}
              </TabsList>
              {visible.map((t) => {
                const Comp = componentMap[t.key];
                return (
                  <TabsContent key={t.key} value={t.key}>
                    <Suspense fallback={<TabFallback />}>{Comp && <Comp />}</Suspense>
                  </TabsContent>
                );
              })}
            </Tabs>
          );
        })()}
      </div>
      </div>
    </div>
  );
}
